import { memo, useCallback, useRef } from 'react';
import { RichTextEditor, RichTextEditorFeatures, RichTextReadOnly, type RichTextEditorRef } from '@brz/rich-text-editor';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import FormHelperText from '@mui/material/FormHelperText';

interface CdfFormattedTextFormFieldProps {
  name: string;
  label?: string;
  required?: boolean;
  readOnly?: boolean;
  errors?: Record<string, string>;
  /** HTML content displayed and edited by the rich-text editor */
  value?: string;
  placeholder?: string;
  /** Minimum height of the editor area, e.g. "120px" */
  editorMinHeight?: string;
  'data-testid'?: string;
  /** Called whenever the content changes. Provides both the plain-text and HTML representations. */
  onUpdate: (name: string, plainText: string, html: string) => void;
}

const EDITOR_FEATURES: RichTextEditorFeatures = {
  images: false
};

export const CdfFormattedTextFormField = memo(function CdfFormattedTextFormField({
  name,
  label,
  required,
  readOnly = false,
  errors = {},
  value,
  placeholder,
  editorMinHeight,
  'data-testid': dataTestId,
  onUpdate
}: Readonly<CdfFormattedTextFormFieldProps>) {
  const { t } = useTranslation();
  const editorRef = useRef<RichTextEditorRef>(null);

  const hasError = !!errors[name];
  const errorMessage = hasError ? t(errors[name]) : '';

  const handleUpdate = useCallback(
    (html: string): void => {
      const plainText = editorRef.current?.getText() ?? '';
      onUpdate(name, plainText, html);
    },
    [name, onUpdate]
  );

  return (
    <Box sx={{ width: '100%' }} data-testid={dataTestId}>
      {label && (
        <Typography component="label" variant="body2" sx={{ display: 'block', mb: 0.5, color: hasError ? 'error.main' : 'text.secondary' }}>
          {label}
          {required && <Typography component="span">{' *'}</Typography>}
        </Typography>
      )}
      {readOnly ? (
        <RichTextReadOnly content={value} />
      ) : (
        <RichTextEditor
          ref={editorRef}
          content={value}
          onUpdate={handleUpdate}
          placeholder={placeholder}
          editorMinHeight={editorMinHeight}
          features={EDITOR_FEATURES}
        />
      )}
      <FormHelperText error={hasError} sx={{ mx: '0.875rem', mt: '0.5rem' }}>
        {errorMessage || ' '}
      </FormHelperText>
    </Box>
  );
});
